import { SectionRoleField } from "./editors/section-role-field";
import { referenceCompanyDomainEditorSchema, referenceCompanyDomainPayload, referenceDomainEditorSchema, toReferenceCompanyDomainEditor, toReferenceDomainEditor, referenceDomainPayload } from "@/lib/adapters/reference-editor";
import { referenceEditorSections } from "@/lib/reference-editor-sections";
import { referenceCompanyEditorSections } from "@/lib/reference-company-editor-sections";
import { ReferenceEditorPanels } from "./editors/reference-editor-sections";
import { ReferenceCompanyEditorPanels } from "./editors/reference-company-editor-sections";
import { DraftSaveButton } from "./workspace/draft-save-button";
import { SectionNavigation } from "./editor/section-navigation";
import * as Tabs from "@radix-ui/react-tabs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Circle,
  CircleAlert,
  Eye,
  FileText,
  Image as ImageIcon,
  Save,
  Scale,
  Search,
  Settings2,
  X,
} from "lucide-react";
import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  toEditorialEditor,
  toFinanceEditor,
  toServiceEditor,
  editorialEditorPayload,
  financeEditorPayload,
  serviceEditorPayload,
} from "@/lib/adapters/editor";
import { apiRequest, ApiError, jsonBody } from "@/lib/api/client";
import type {
  EditorialEditorModel,
  FinanceEditorModel,
  ServiceEditorModel,
  ReferenceDomainEditorModel,
  ReferenceCompanyDomainEditorModel,
} from "@/lib/domain/editor";
import {
  editorialEditorSchema,
  financeEditorSchema,
  serviceEditorSchema,
} from "@/lib/schemas/editor";
import { workflowSchema } from "@/lib/schemas/common";
import { DraftPreview } from "./draft-preview";
import { EditorConflictPanel } from "./editor-conflict-panel";
import { CompletionStatus } from "./editor/completion-status";
import {
  DomainRelations,
  EditorialFormatFields,
} from "./editors/domain-sections";
import { FinanceEditorPanels } from "./editors/finance-editor-sections";
import { ServiceEditorPanels } from "./editors/service-editor-sections";
import { VideoBlockEditor } from "./video-block-editor";
import { MediaPicker } from "./media-picker";
import { RichTextEditor } from "./rich-text-editor";
import { StatusBadge } from "./status-badge";
import { WorkflowControls } from "./workflow-controls";
import { canSaveDraft } from "./workflow-actions";
import { Button, IconButton } from "./ui/controls";
import { LanguageTabs } from "./ui/language-tabs";
import { AlertDialog, Dialog, Drawer } from "./ui/overlays";
import { useToast } from "./ui/toast-context";
import { WorkspaceError, WorkspaceLoading } from "./workspace/workspace-state";
import { DecisionRibbon } from "./workspace/decision-ribbon";
import { EditorHeader } from "./workspace/editor-header";
import { StickySaveBar } from "./workspace/sticky-save-bar";
import { EditorWorkspace as EditorWorkspaceFrame } from "./workspace/editor-workspace";
import editorStyles from "./editor/editor-workspace.module.css";
import { getCompletionResult } from "@/lib/editor-completion";

export type DomainEditorMode = "service" | "finance" | "editorial" | "reference" | "referenceCompany";
type Mode = DomainEditorMode;
type EditorModel =
  | ReferenceDomainEditorModel
  | ReferenceCompanyDomainEditorModel
  | ServiceEditorModel
  | FinanceEditorModel
  | EditorialEditorModel;

function revisionChangeLabels(values: readonly string[]) {
  const labels = values.map((value) => {
    if (value === "created") return "İlk taslak";
    if (value === "restored_revision") return "Önceki sürüm geri yüklendi";
    if (value.startsWith("translation") || value.startsWith("fields")) return "Metinler";
    if (value.startsWith("canonical") || value.startsWith("record")) return "Yapı ve ayarlar";
    if (value.startsWith("content")) return "Detay içeriği";
    if (value.startsWith("relations")) return "İlişkiler";
    if (value.includes("media") || value.includes("image")) return "Medya";
    return "İçerik güncellendi";
  });
  return [...new Set(labels)];
}

const configuration = {
  reference: {base:"/referanslar",endpoint:"references",eyebrow:"Vaka çalışması yönetimi",itemKinds:[]},
  referenceCompany: {base:"/referanslar",endpoint:"reference-companies",eyebrow:"Referans şirketi",itemKinds:[]},
  service: {
    base: "/hizmetler",
    endpoint: "services",
    eyebrow: "Hizmet yönetimi",
    itemKinds: [
      { value: "benefit", label: "Fayda" },
      { value: "process", label: "Süreç" },
      { value: "approach", label: "Yaklaşım" },
      { value: "faq", label: "SSS" },
    ],
  },
  finance: {
    base: "/finansman",
    endpoint: "finance-solutions",
    eyebrow: "Finansman yönetimi",
    itemKinds: [
      { value: "criterion", label: "Çözüm özelliği" },
      { value: "evaluation_step", label: "Değerlendirme adımı" },
      { value: "faq", label: "SSS" },
    ],
  },
  editorial: {
    base: "/editorial",
    endpoint: "editorial",
    eyebrow: "Editoryal yönetim",
    itemKinds: [],
  },
} as const;

async function fetchEditor(
  mode: Mode,
  id: string,
  locale: string,
): Promise<EditorModel> {
  const config = configuration[mode];
  if(mode === "reference") return toReferenceDomainEditor((await apiRequest(`content/references/${id}/editor/?locale=${locale}`,referenceDomainEditorSchema)).data);
  if(mode === "referenceCompany") return toReferenceCompanyDomainEditor((await apiRequest(`content/reference-companies/${id}/editor/?locale=${locale}`,referenceCompanyDomainEditorSchema)).data);
  if (mode === "service")
    return toServiceEditor(
      (
        await apiRequest(
          `content/${config.endpoint}/${id}/editor/?locale=${locale}`,
          serviceEditorSchema,
        )
      ).data,
    );
  if (mode === "finance")
    return toFinanceEditor(
      (
        await apiRequest(
          `content/${config.endpoint}/${id}/editor/?locale=${locale}`,
          financeEditorSchema,
        )
      ).data,
    );
  return toEditorialEditor(
    (
      await apiRequest(
        `content/${config.endpoint}/${id}/editor/?locale=${locale}`,
        editorialEditorSchema,
      )
    ).data,
  );
}

async function persistEditor(
  mode: Mode,
  model: EditorModel,
): Promise<EditorModel> {
  const config = configuration[mode];
  if(mode === "reference" && model.kind === "reference") return toReferenceDomainEditor((await apiRequest(`content/references/${model.id}/editor/`,referenceDomainEditorSchema,{method:"PATCH",...jsonBody(referenceDomainPayload(model))})).data);
  if(mode === "referenceCompany" && model.kind === "referenceCompany") return toReferenceCompanyDomainEditor((await apiRequest(`content/reference-companies/${model.id}/editor/`,referenceCompanyDomainEditorSchema,{method:"PATCH",...jsonBody(referenceCompanyDomainPayload(model))})).data);
  if (mode === "service" && model.kind === "service")
    return toServiceEditor(
      (
        await apiRequest(
          `content/${config.endpoint}/${model.id}/editor/`,
          serviceEditorSchema,
          { method: "PATCH", ...jsonBody(serviceEditorPayload(model)) },
        )
      ).data,
    );
  if (mode === "finance" && model.kind === "finance")
    return toFinanceEditor(
      (
        await apiRequest(
          `content/${config.endpoint}/${model.id}/editor/`,
          financeEditorSchema,
          { method: "PATCH", ...jsonBody(financeEditorPayload(model)) },
        )
      ).data,
    );
  if (mode === "editorial" && model.kind === "editorial")
    return toEditorialEditor(
      (
        await apiRequest(
          `content/${config.endpoint}/${model.id}/editor/`,
          editorialEditorSchema,
          { method: "PATCH", ...jsonBody(editorialEditorPayload(model)) },
        )
      ).data,
    );
  throw new Error("Editör türü eşleşmiyor.");
}

function Field({
  label,
  children,
  help,
  className,
}: {
  label: string;
  children: React.ReactNode;
  help?: string;
  className?: string;
}) {
  const generatedId = useId();
  const control = isValidElement(children)
    ? (children as ReactElement<{ id?: string }>)
    : null;
  const controlId = control?.props.id ?? generatedId;
  return (
    <div className={`ui-field${className ? ` ${className}` : ""}`}>
      <label htmlFor={controlId}>{label}</label>
      {control ? cloneElement(control, { id: controlId }) : children}
      {help && <small>{help}</small>}
    </div>
  );
}

export function DomainEditorWorkspace({
  mode,
  id,
  returnTo,
}: {
  mode: Mode;
  id: string;
  returnTo?: string;
}) {
  const [searchParams] = useSearchParams();
  const locale = searchParams.get("locale") || "tr";
  const query = useQuery({
    queryKey: ["editor", mode, id, locale],
    queryFn: () => fetchEditor(mode, id, locale),
  });
  if (query.isLoading)
    return <WorkspaceLoading label="İçerik editörü yükleniyor" lines={8} />;
  if (query.isError || !query.data)
    return (
      <WorkspaceError
        title="Editör yüklenemedi"
        description="Kayıt şu anda alınamıyor. Bağlantıyı yenileyip tekrar deneyin."
        onRetry={() => void query.refetch()}
      />
    );
  return (
    <EditorWorkspace
      key={`${mode}:${id}:${locale}:${query.data.version}`}
      mode={mode}
      locale={locale}
      initialModel={query.data}
      returnTo={returnTo}
    />
  );
}

function EditorWorkspace({
  mode,
  locale,
  initialModel,
  returnTo,
}: {
  mode: Mode;
  locale: string;
  initialModel: EditorModel;
  returnTo?: string;
}) {
  const config = configuration[mode];
  const client = useQueryClient();
  const notify = useToast();
  const [editorSearchParams, setSearchParams] = useSearchParams();
  const reviewMode = editorSearchParams.get("review") === "1";
  const [model, setModel] = useState<EditorModel>(initialModel);
  const [baseModel, setBaseModel] = useState<EditorModel>(initialModel);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [conflictServer, setConflictServer] = useState<EditorModel | null>(null);
  const [publishDrawerOpen, setPublishDrawerOpen] = useState(reviewMode);
  const [reviewNoticeOpen, setReviewNoticeOpen] = useState(false);
  const [pendingLocale, setPendingLocale] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (override?: EditorModel) => {
      return persistEditor(mode, override ?? model);
    },
    onSuccess: (next) => {
      setModel(next);
      setBaseModel(next);
      setDirty(false);
      setMessage("Taslak kaydedildi");
      setConflictServer(null);
      setError("");
      notify({ title: "Taslak kaydedildi", tone: "success" });
      client.invalidateQueries({ queryKey: [config.endpoint] });
      if (mode === "reference" || mode === "referenceCompany") client.invalidateQueries({ queryKey: ["corporate"] });
    },
    onError: async (reason) => {
      setMessage("");
      if (reason instanceof ApiError && reason.code === "version_conflict") {
        try {
          setConflictServer(await fetchEditor(mode, model.id, locale));
        } catch {
          setConflictServer(null);
        }
      }
      const description =
        reason instanceof ApiError ? reason.message : "Taslak kaydedilemedi.";
      setError(description);
      notify({ title: "Taslak kaydedilemedi", description, tone: "danger" });
    },
  });
  const restore = useMutation({
    mutationFn: async (revisionVersion: number) => {
      if (!model.workflow.id) throw new Error("Yayın kaydı bulunamadı.");
      return apiRequest(`workflow/${model.workflow.id}/`, workflowSchema, {
        method: "POST",
        ...jsonBody({
          action: "restore",
          version: model.workflow.version,
          revision_version: revisionVersion,
        }),
      });
    },
    onSuccess: () => {
      setMessage("Revizyon yeni taslak olarak geri yüklendi");
      notify({ title: "Sürüm yeni taslak olarak geri yüklendi", tone: "success" });
      client.invalidateQueries({
        queryKey: ["editor", mode, model.id, locale],
      });
    },
    onError: (reason) => {
      const description =
        reason instanceof ApiError
          ? reason.message
          : "Revizyon geri yüklenemedi.";
      setError(description);
      notify({ title: "Sürüm geri yüklenemedi", description, tone: "danger" });
    },
  });
  useEffect(() => {
    if (
      !dirty ||
      save.isPending ||
      conflictServer ||
      !canSaveDraft(model.workflow.actions)
    )
      return;
    const timer = window.setTimeout(() => save.mutate(model), 2000);
    return () => window.clearTimeout(timer);
  }, [conflictServer, dirty, model, save]);
  useEffect(() => {
    if (!dirty) return;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [dirty]);
  function change(updater: (current: EditorModel) => EditorModel) {
    setModel((current) => updater(current));
    setDirty(true);
    setMessage("Kaydedilmemiş değişiklikler");
  }
  function applyWorkflowUpdate(workflow: EditorModel["workflow"]) {
    setModel((current) => ({
      ...current,
      workflow,
      allowedActions: [...workflow.actions],
      version: workflow.version,
    }));
  }
  function switchLocale(code: string) {
    const next = new URLSearchParams(editorSearchParams);
    next.set("locale", code);
    setSearchParams(next, { replace: true });
  }
  function requestLocaleSwitch(code: string) {
    if (code === locale) return;
    if (dirty) {
      setPendingLocale(code);
      return;
    }
    switchLocale(code);
  }
  async function confirmLocaleSwitch() {
    if (!pendingLocale) return;
    const target = pendingLocale;
    try {
      await save.mutateAsync(model);
      setPendingLocale(null);
      switchLocale(target);
    } catch {
      // Mutation feedback is normalized by the shared save handler.
    }
  }
  const title = model.translation.title || "İçerik editörü";
  const body =
    model.kind === "service"
      ? model.content.body
      : model.kind === "finance"
        ? model.translation.body
        : model.content.body || "";
  const languageState = useMemo(
    () => model.localeStates.find((item) => item.code === locale),
    [model, locale],
  );
  const financeSections = new Set(["overview", "content", "criteria", "process", "video", "faq", "relations", "seo", "preview", "common"]);
  const serviceSections = new Set(["finance_models", "overview", "content", "benefits", "process", "video", "faq", "related", "seo", "preview", "common"]);
  const genericSections = new Set(["content", "structure", "media", "seo", "preview"]);
  const editorialSections = new Set(["content", "video", "related", "media", "seo", "preview"]);
  const defaultSection = model.kind === "reference" || model.kind === "referenceCompany" ? "overview" : model.kind === "finance" || model.kind === "service" ? "overview" : "content";
  const validSections = model.kind === "reference" ? new Set(referenceEditorSections.map(([key])=>key)) : model.kind === "referenceCompany" ? new Set(referenceCompanyEditorSections.map(([key])=>key)) : model.kind === "finance" ? financeSections : model.kind === "service" ? serviceSections : model.kind === "editorial" ? editorialSections : genericSections;
  const requestedSection = editorSearchParams.get("section") || defaultSection;
  const activeSection = validSections.has(requestedSection) ? requestedSection : defaultSection;
  const qualityChecks = useMemo(
    () => {
      if(model.kind === "reference") return [
        {key:"title",label:"Başlık tamamlandı",complete:Boolean(model.translation.title.trim()),section:"overview"},
        {key:"slug",label:"URL adı tamamlandı",complete:Boolean(model.translation.slug),section:"overview"},
        {key:"image",label:"Görsel ve alternatif metin hazır",complete:Boolean(model.record.mediaAssetId && model.translation.imageAlt),section:"overview"},
        {key:"summary",label:"Vaka anlatısı eklendi",complete:Boolean(model.translation.summary),section:"content"},
        {key:"sector",label:"Sektör seçildi",complete:Boolean(model.relations.sectorId),section:"relations"},
        {key:"services",label:"Hizmetler seçildi",complete:Boolean(model.relations.serviceIds.length),section:"relations"},
        {key:"seo",label:"Arama görünümü hazır",complete:Boolean(model.translation.seoTitle && model.translation.seoDescription),section:"seo"},
      ];
      if(model.kind === "referenceCompany") return [
        {key:"title",label:"Şirket adı tamamlandı",complete:Boolean(model.record.organizationName.trim()),section:"overview"},
        {key:"slug",label:"URL adı tamamlandı",complete:Boolean(model.translation.slug),section:"overview"},
        {key:"logo",label:"Logo ve alternatif metin hazır",complete:Boolean(model.record.mediaAssetId && model.translation.imageAlt),section:"overview"},
        {key:"summary",label:"Kısa açıklama tamamlandı",complete:Boolean(model.translation.summary),section:"content"},
        {key:"body",label:"Detay içeriği eklendi",complete:Boolean(model.content.body),section:"content"},
        {key:"sector",label:"Sektör seçildi",complete:Boolean(model.relations.sectorId),section:"overview"},
        {key:"seo",label:"Arama görünümü hazır",complete:Boolean(model.translation.seoTitle && model.translation.seoDescription),section:"seo"},
      ];
      if (model.kind === "finance") return [
        { key: "category", label: "Finansman kategorisi seçildi", complete: Boolean(model.record.categoryId), section: "overview" },
        { key: "title", label: "Başlık tamamlandı", complete: Boolean(model.translation.title.trim()), section: "overview" },
        { key: "slug", label: "URL adı tamamlandı", complete: Boolean(model.translation.slug.trim()), section: "overview" },
        { key: "summary", label: "Kısa açıklama tamamlandı", complete: Boolean(model.translation.summary.trim()), section: "overview" },
        { key: "image", label: "Görsel ve alternatif metin hazır", complete: Boolean(model.record.mediaAssetId ? model.translation.imageAlt.trim() : true), section: "overview" },
        { key: "body", label: "Detay içeriği eklendi", complete: Boolean(body.trim()), section: "content" },
        { key: "seo", label: "Arama görünümü hazır", complete: Boolean(model.translation.seoTitle.trim() && model.translation.seoDescription.trim()), section: "seo" },
      ];
      return [
        { key: "category", label: "Hizmet kategorisi seçildi", complete: model.kind !== "service" || Boolean(model.record.categoryId), section: model.kind === "service" ? "overview" : "content" },
        { key: "title", label: "Başlık tamamlandı", complete: Boolean(model.translation.title.trim()), section: model.kind === "service" ? "overview" : "content" },
        { key: "slug", label: "URL adı tamamlandı", complete: Boolean(model.translation.slug.trim()), section: model.kind === "service" ? "overview" : "content" },
        { key: "summary", label: "Kısa açıklama tamamlandı", complete: Boolean(model.translation.summary.trim()), section: model.kind === "service" ? "overview" : "content" },
        { key: "body", label: "Detay içeriği eklendi", complete: Boolean(body.trim()), section: "content" },
        { key: "image", label: "Kapak ve alternatif metin hazır", complete: Boolean(model.record.mediaAssetId ? model.translation.imageAlt.trim() : true), section: "media" },
        { key: "seo", label: "Arama görünümü hazır", complete: Boolean(model.translation.seoTitle.trim() && model.translation.seoDescription.trim()), section: "seo" },
      ];
    },
    [body, model],
  );
  const incompleteBySection = qualityChecks.reduce<Record<string, number>>((result, item) => {
    if (!item.complete) result[item.section] = (result[item.section] || 0) + 1;
    return result;
  }, {});
  const completedChecks = qualityChecks.filter((item) => item.complete).length;
  const completionFor = (section: string) => {
    if (model.kind === "finance" && section === "criteria") return getCompletionResult([{ label: "En az bir çözüm özelliği", value: model.content.items.some((item) => item.kind === "criterion" && item.title.trim()) }]);
    if (model.kind === "finance" && section === "process") return getCompletionResult([{ label: "En az bir değerlendirme adımı", value: model.content.items.some((item) => item.kind === "evaluation_step" && item.title.trim()) }]);
    if (model.kind === "finance" && section === "faq") return getCompletionResult([{ label: "En az bir soru ve cevap", value: model.content.items.some((item) => item.kind === "faq" && item.title.trim() && item.description.trim()) }]);
    if (model.kind === "service" && section === "benefits") return getCompletionResult([{ label: "En az bir fayda", value: model.content.items.some((item) => item.kind === "benefit" && item.title.trim()) }]);
    if (model.kind === "service" && section === "process") return getCompletionResult([{ label: "En az bir süreç adımı", value: model.content.items.some((item) => item.kind === "process" && item.title.trim()) }]);
    if (model.kind === "service" && section === "faq") return getCompletionResult([{ label: "En az bir soru ve cevap", value: model.content.items.some((item) => item.kind === "faq" && item.title.trim() && item.description.trim()) }]);
    return getCompletionResult(
      qualityChecks
        .filter((item) => item.section === section)
        .map((item) => ({ label: item.label, value: item.complete })),
    );
  };
  const workflowDisabledReason = conflictServer
    ? "Önce sürüm çakışmasını çözün."
    : save.isPending
      ? "Taslak kaydediliyor."
      : dirty
        ? "Önce taslağın kaydedilmesini bekleyin."
        : undefined;
  const changeSection = (section: string) => {
    const next = new URLSearchParams(editorSearchParams);
    next.set("section", section);
    setSearchParams(next, { replace: true });
  };
  return (
    <EditorWorkspaceFrame className={editorStyles.page} ariaLabel={`${title} editörü`}>
      <EditorHeader
        backTo={returnTo ?? config.base}
        eyebrow={config.eyebrow}
        title={title}
        actions={<>
          {canSaveDraft(model.workflow.actions) ? <DraftSaveButton dirty={dirty} pending={save.isPending} blocked={Boolean(conflictServer)} onSave={() => save.mutate(model)} /> : null}
          <LanguageTabs
          variant="compact"
          items={model.localeStates.map((state) => ({
            code: state.code,
            label: state.label,
            complete: state.hasTranslation,
            status: state.hasTranslation ? state.workflow.label : "İçerik eksik",
          }))}
          value={locale}
          onChange={requestLocaleSwitch}
        /></>}
      />
      <DecisionRibbon
        status={<StatusBadge>{model.workflow.label}</StatusBadge>}
        locale={languageState?.label || locale.toUpperCase()}
        version={model.version}
        completedChecks={completedChecks}
        totalChecks={qualityChecks.length}
        dirty={dirty}
        nextActionLabel={reviewMode ? "İnceleme özeti" : "Yayın özeti"}
        onNextAction={() => setPublishDrawerOpen(true)}
      />
      {error && <div className={editorStyles.alert} role="alert">{error}</div>}
      {conflictServer ? (
        <EditorConflictPanel
          key={conflictServer.version}
          base={baseModel}
          local={model}
          server={conflictServer}
          busy={save.isPending}
          onUseServer={(server) => {
            setModel(server);
            setBaseModel(server);
            setConflictServer(null);
            setDirty(false);
            setError("");
            setMessage("Güncel sürüm yüklendi");
          }}
          onResolve={(merged) => {
            setBaseModel(conflictServer);
            setModel(merged);
            setConflictServer(null);
            setDirty(true);
            setError("");
            save.mutate(merged);
          }}
        />
      ) : null}
      <Tabs.Root className={editorStyles.tabs} value={activeSection} onValueChange={changeSection}>
        <div className={editorStyles.mobileSectionPicker}>
          <label htmlFor="editor-section">Düzenleme bölümü</label>
          <select id="editor-section" className="ui-select" value={activeSection} onChange={(event) => changeSection(event.target.value)}>
            {model.kind === "reference" ? <><option value="overview">Temel bilgiler</option><optgroup label="İçerik bölümleri">{[...model.content.sections].sort((a,b)=>a.order-b.order).map(section=><option key={section.id} value={section.key}>{({content:"Detay içeriği",metrics:"Metrikler",video:"Video",relations:"Sektör ve hizmetler"} as Record<string,string>)[section.key] || section.key}</option>)}</optgroup><option value="seo">SEO</option><option value="preview">Önizleme</option></> : model.kind === "referenceCompany" ? referenceCompanyEditorSections.map(([key,label])=><option key={key} value={key}>{label}</option>) : model.kind === "finance" ? <>
              <option value="overview">Temel bilgiler</option>
              <option value="content">Detay içeriği</option>
              <optgroup label="İçerik bölümleri">
                <option value="criteria">Çözüm özellikleri</option>
                <option value="process">Değerlendirme adımları</option>
                {model.content.sections.some((item)=>item.key === "video") ? <option value="video">Video</option> : null}
                <option value="faq">Sık sorulan sorular</option>
              </optgroup>
              <option value="relations">Karşılaştırma kriterleri</option>
              <option value="seo">SEO</option>
              <option value="preview">Önizleme</option>
              <option value="common">Ortak öğeler</option>
            </> : model.kind === "service" ? <>
              <option value="overview">Temel bilgiler</option><option value="content">Detay içeriği</option>
              <optgroup label="İçerik bölümleri"><option value="benefits">Hizmetin faydaları</option><option value="process">Süreç adımları</option>{model.kind === "service" && model.content.sections.some((item)=>item.key === "video") ? <option value="video">Video</option> : null}<option value="faq">Sık sorulan sorular</option></optgroup>
              <option value="related">İlgili hizmetler</option><option value="finance_models">İlgili finansman çözümleri</option><option value="seo">SEO</option><option value="preview">Önizleme</option><option value="common">Ortak öğeler</option>
            </> : model.kind === "editorial" ? <>
              <optgroup label="İçerik bölümleri">{[...model.content.sections].sort((a,b)=>a.order-b.order).map(section=><option key={section.id} value={section.key}>{({content:"Detay içeriği",video:"Video",related:"İlgili içerikler"} as Record<string,string>)[section.key] || section.key}</option>)}</optgroup>
              <option value="media">Medya</option><option value="seo">SEO</option><option value="preview">Önizleme</option>
            </> : <>
              <option value="content">İçerik</option>
              <option value="structure">Yapı ve ilişkiler</option>
              <option value="media">Medya</option>
              <option value="seo">SEO</option>
              <option value="preview">Önizleme</option>
            </>}
          </select>
        </div>
        <div className={editorStyles.layout} data-editor-layout>
          <Tabs.List className={editorStyles.navigation} data-editor-navigation aria-label="Editör bölümleri">
            {model.kind === "reference" ? <>
              <div className={editorStyles.navigationHeader}><span>Referans yapısı</span><small>Düzenleme alanları</small></div>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="overview"><span className={editorStyles.navigationIcon}><ImageIcon size={15}/></span><span>Temel bilgiler</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("overview")}/></Tabs.Trigger>
              <div className={editorStyles.navigationGroup}><div className={editorStyles.navigationGroupLabel}><span>İçerik</span><small>{model.content.sections.length} bölüm</small></div><SectionNavigation items={[...model.content.sections].sort((a,b)=>a.order-b.order).map(item=>({id:item.id,active:item.active,tab:item.key,label:({content:"Detay içeriği",metrics:"Metrikler",video:"Video",relations:"Sektör ve hizmetler"} as Record<string,string>)[item.key] || item.key}))} onOrder={ids=>change(current=>current.kind==="reference"?({...current,content:{...current.content,sections:ids.map((id,order)=>({...current.content.sections.find(item=>item.id===id)!,order}))}}):current)} onVisibility={(id,active)=>change(current=>current.kind==="reference"?({...current,content:{...current.content,sections:current.content.sections.map(item=>item.id===id?{...item,active}:item)}}):current)}/></div>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="seo"><span className={editorStyles.navigationIcon}><Search size={15}/></span><span>SEO</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("seo")}/></Tabs.Trigger>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="preview"><span className={editorStyles.navigationIcon}><Eye size={15}/></span><span>Önizleme</span></Tabs.Trigger>
            </> : model.kind === "referenceCompany" ? <>
              <div className={editorStyles.navigationHeader}><span>Şirket profili</span><small>Düzenleme alanları</small></div>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="overview"><span className={editorStyles.navigationIcon}><ImageIcon size={15}/></span><span>Temel bilgiler</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("overview")}/></Tabs.Trigger>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="content"><span className={editorStyles.navigationIcon}><FileText size={15}/></span><span>Detay içeriği</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("content")}/></Tabs.Trigger>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="relations"><span className={editorStyles.navigationIcon}><Scale size={15}/></span><span>Bağlı vakalar</span></Tabs.Trigger>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="seo"><span className={editorStyles.navigationIcon}><Search size={15}/></span><span>SEO</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("seo")}/></Tabs.Trigger>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="preview"><span className={editorStyles.navigationIcon}><Eye size={15}/></span><span>Önizleme</span></Tabs.Trigger>
            </> : model.kind === "finance" ? <>
              <div className={editorStyles.navigationHeader}><span>Çözüm yapısı</span><small>Düzenleme alanları</small></div>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="overview"><span className={editorStyles.navigationIcon}><ImageIcon size={15}/></span><span>Temel bilgiler</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("overview")}/></Tabs.Trigger>
              {model.content.sections.length ? <div className={editorStyles.navigationGroup}>
                <div className={editorStyles.navigationGroupLabel}><span>İçerik</span><small>{model.content.sections.length} bölüm</small></div>
                <SectionNavigation
                  items={[...model.content.sections].sort((a,b)=>a.order-b.order).map((item)=>({id:item.id,active:item.active,tab:item.key,label:({content:"Detay içeriği",criteria:"Çözüm özellikleri",process:"Değerlendirme adımları",video:"Video",faq:"Sık sorulan sorular"} as Record<string,string>)[item.key] || item.key}))}
                  onOrder={(ids)=>change((current)=>current.kind === "finance" ? ({...current,content:{...current.content,sections:ids.map((id,order)=>({...current.content.sections.find((item)=>item.id===id)!,order}))}}) : current)}
                  onVisibility={(id,active)=>change((current)=>current.kind === "finance" ? ({...current,content:{...current.content,sections:current.content.sections.map((item)=>item.id===id?{...item,active}:item)}}) : current)}
                />
              </div> : <Tabs.Trigger className={editorStyles.navigationPrimary} value="content"><span className={editorStyles.navigationIcon}><FileText size={15}/></span><span>Detay içeriği</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("content")}/></Tabs.Trigger>}
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="relations"><span className={editorStyles.navigationIcon}><Scale size={15}/></span><span>Karşılaştırma kriterleri</span></Tabs.Trigger>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="seo"><span className={editorStyles.navigationIcon}><Search size={15}/></span><span>SEO</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("seo")}/></Tabs.Trigger>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="preview"><span className={editorStyles.navigationIcon}><Eye size={15}/></span><span>Önizleme</span></Tabs.Trigger>
              <div className={editorStyles.navigationShared}>
                <div className={editorStyles.navigationSharedLabel}><span>Ortak yapı</span><small>Tüm finansman detayları</small></div>
                <Tabs.Trigger className={editorStyles.navigationCommon} value="common"><span className={editorStyles.navigationIcon}><Settings2 size={15}/></span><span>Ortak öğeler</span></Tabs.Trigger>
              </div>
            </> : model.kind === "service" ? <>
              <div className={editorStyles.navigationHeader}><span>Hizmet yapısı</span><small>Düzenleme alanları</small></div>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="overview"><span className={editorStyles.navigationIcon}><ImageIcon size={15}/></span><span>Temel bilgiler</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("overview")}/></Tabs.Trigger>
              {model.content.sections.length ? <div className={editorStyles.navigationGroup}>
                <div className={editorStyles.navigationGroupLabel}><span>İçerik</span><small>{model.content.sections.length} bölüm</small></div>
                <SectionNavigation
                  items={[...model.content.sections].sort((a,b)=>a.order-b.order).map((item)=>({id:item.id,active:item.active,tab:item.key,label:({content:"Detay içeriği",benefits:"Hizmetin faydaları",process:"Süreç adımları",video:"Video",finance_models:"İlgili finansman çözümleri",related:"İlgili hizmetler",faq:"Sık sorulan sorular"} as Record<string,string>)[item.key] || item.key}))}
                  onOrder={(ids)=>change((current)=>current.kind === "service" ? ({...current,content:{...current.content,sections:ids.map((id,order)=>({...current.content.sections.find((item)=>item.id===id)!,order}))}}) : current)}
                  onVisibility={(id,active)=>change((current)=>current.kind === "service" ? ({...current,content:{...current.content,sections:current.content.sections.map((item)=>item.id===id?{...item,active}:item)}}) : current)}
                />
              </div> : <>
                <Tabs.Trigger className={editorStyles.navigationPrimary} value="content"><span className={editorStyles.navigationIcon}><FileText size={15}/></span><span>Detay içeriği</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("content")}/></Tabs.Trigger>
                <Tabs.Trigger className={editorStyles.navigationPrimary} value="benefits"><span className={editorStyles.navigationIcon}><FileText size={15}/></span><span>Hizmetin faydaları</span></Tabs.Trigger>
                <Tabs.Trigger className={editorStyles.navigationPrimary} value="process"><span className={editorStyles.navigationIcon}><FileText size={15}/></span><span>Süreç adımları</span></Tabs.Trigger>
                <Tabs.Trigger className={editorStyles.navigationPrimary} value="faq"><span className={editorStyles.navigationIcon}><FileText size={15}/></span><span>Sık sorulan sorular</span></Tabs.Trigger>
                <Tabs.Trigger className={editorStyles.navigationPrimary} value="related"><span className={editorStyles.navigationIcon}><Scale size={15}/></span><span>İlgili hizmetler</span></Tabs.Trigger>
                <Tabs.Trigger className={editorStyles.navigationPrimary} value="finance_models"><span className={editorStyles.navigationIcon}><Scale size={15}/></span><span>İlgili finansman çözümleri</span></Tabs.Trigger>
              </>}
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="seo"><span className={editorStyles.navigationIcon}><Search size={15}/></span><span>SEO</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("seo")}/></Tabs.Trigger>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="preview"><span className={editorStyles.navigationIcon}><Eye size={15}/></span><span>Önizleme</span></Tabs.Trigger>
              <div className={editorStyles.navigationShared}><div className={editorStyles.navigationSharedLabel}><span>Ortak yapı</span><small>Tüm hizmet detayları</small></div><Tabs.Trigger className={editorStyles.navigationCommon} value="common"><span className={editorStyles.navigationIcon}><Settings2 size={15}/></span><span>Ortak öğeler</span></Tabs.Trigger></div>
            </> : model.kind === "editorial" ? <>
              <div className={editorStyles.navigationHeader}><span>İçerik yapısı</span><small>Düzenleme alanları</small></div>
              <div className={editorStyles.navigationGroup}><div className={editorStyles.navigationGroupLabel}><span>İçerik</span><small>{model.content.sections.length} bölüm</small></div><SectionNavigation
                items={[...model.content.sections].sort((a,b)=>a.order-b.order).map(item=>({id:item.id,active:item.active,tab:item.key,label:({content:"Detay içeriği",video:"Video",related:"İlgili içerikler"} as Record<string,string>)[item.key] || item.key}))}
                onOrder={ids=>{change(current=>current.kind==="editorial"?({...current,content:{...current.content,sections:ids.map((id,order)=>({...current.content.sections.find(item=>item.id===id)!,order}))}}):current);notify({title:"Bölüm sırası değişti · Kaydedin",tone:"warning"});}}
                onVisibility={(id,active)=>{change(current=>current.kind==="editorial"?({...current,content:{...current.content,sections:current.content.sections.map(item=>item.id===id?{...item,active}:item)}}):current);notify({title:`Bölüm ${active?"gösterilecek":"gizlenecek"} · Kaydedin`,tone:"warning"});}}
              /></div>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="media"><span className={editorStyles.navigationIcon}><ImageIcon size={15}/></span><span>Medya</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("media")}/></Tabs.Trigger>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="seo"><span className={editorStyles.navigationIcon}><Search size={15}/></span><span>SEO</span><CompletionStatus className={editorStyles.navigationCompletion} result={completionFor("seo")}/></Tabs.Trigger>
              <Tabs.Trigger className={editorStyles.navigationPrimary} value="preview"><span className={editorStyles.navigationIcon}><Eye size={15}/></span><span>Önizleme</span></Tabs.Trigger>
            </> : <>
              <Tabs.Trigger value="content">İçerik{incompleteBySection.content ? <span className={editorStyles.issueCount}>{incompleteBySection.content}</span> : null}</Tabs.Trigger>
              <Tabs.Trigger value="structure">Yapı ve ilişkiler</Tabs.Trigger>
              <Tabs.Trigger value="media">Medya{incompleteBySection.media ? <span className={editorStyles.issueCount}>{incompleteBySection.media}</span> : null}</Tabs.Trigger>
              <Tabs.Trigger value="seo">SEO{incompleteBySection.seo ? <span className={editorStyles.issueCount}>{incompleteBySection.seo}</span> : null}</Tabs.Trigger>
              <Tabs.Trigger value="preview">Önizleme</Tabs.Trigger>
            </>}
          </Tabs.List>
          <div className={editorStyles.content} data-editor-content>
          {model.kind === "reference" ? <ReferenceEditorPanels model={model} previewDisabled={dirty || save.isPending} change={updater=>change(current=>current.kind === "reference" ? updater(current) : current)}/> : model.kind === "referenceCompany" ? <ReferenceCompanyEditorPanels model={model} previewDisabled={dirty || save.isPending} change={updater=>change(current=>current.kind === "referenceCompany" ? updater(current) : current)}/> : model.kind === "finance" ? (
            <FinanceEditorPanels
              model={model}
              locale={locale}
              previewDisabled={dirty || save.isPending}
              change={(updater) => change((current) => current.kind === "finance" ? updater(current) : current)}
            />
          ) : model.kind === "service" ? (
            <ServiceEditorPanels model={model} locale={locale} previewDisabled={dirty || save.isPending} change={(updater) => change((current) => current.kind === "service" ? updater(current) : current)}/>
          ) : <>
          <Tabs.Content value="content" className="editor-tab-panel">
            <section className={editorStyles.section}>
              <div className="section-intro">
                <h2>Temel içerik</h2>
                <p>
                  Bu dilde ziyaretçilere gösterilecek başlık ve anlatımı
                  düzenleyin.
                </p>
              </div>
              <div className="form-grid">
                <Field label="Başlık">
                  <input
                    className="ui-input"
                    value={model.translation.title}
                    onChange={(event) =>
                      change(
                        (current) =>
                          ({
                            ...current,
                            translation: {
                              ...current.translation,
                              title: event.target.value,
                            },
                          }) as EditorModel,
                      )
                    }
                  />
                </Field>
                <Field label="URL adı">
                  <input
                    className="ui-input"
                    value={model.translation.slug}
                    onChange={(event) =>
                      change(
                        (current) =>
                          ({
                            ...current,
                            translation: {
                              ...current.translation,
                              slug: event.target.value,
                            },
                          }) as EditorModel,
                      )
                    }
                  />
                </Field>
                <Field label="Kısa açıklama" className="full">
                  <textarea
                    className="ui-textarea"
                    value={model.translation.summary}
                    onChange={(event) =>
                      change(
                        (current) =>
                          ({
                            ...current,
                            translation: {
                              ...current.translation,
                              summary: event.target.value,
                            },
                          }) as EditorModel,
                      )
                    }
                  />
                </Field>
                {model.kind === "editorial" && (
                  <EditorialFormatFields model={model} change={updater=>change(current=>current.kind === "editorial" ? updater(current) : current)} />
                )}
                {model.kind === "editorial" && model.content.sections.length ? (
                  <div className="full form-grid">
                    {model.content.sections.filter((section) => section.active).map((section) => (
                      <div key={section.id} style={{display:"flex",alignItems:"end",gap:12,flexWrap:"wrap"}}><SectionRoleField value={section.role} onChange={(role) => change((current) => current.kind === "editorial" ? ({ ...current, content: { ...current.content, sections: current.content.sections.map((item) => item.id === section.id ? { ...item, role } : item) } }) : current)}/><Field label={`${({ content: "İçerik", video: "Video", related: "İlgili içerikler" } as Record<string, string>)[section.key] || section.key} başlık hizası`}>
                        <select className="ui-select" value={section.textAlignment} onChange={(event) => change((current) => current.kind === "editorial" ? ({ ...current, content: { ...current.content, sections: current.content.sections.map((item) => item.id === section.id ? { ...item, textAlignment: event.target.value as "left" | "center" | "right" } : item) } }) : current)}>
                          <option value="left">Sol</option><option value="center">Orta</option><option value="right">Sağ</option>
                        </select>
                      </Field></div>
                    ))}
                  </div>
                ) : null}
                <div className="ui-field full">
                  <label htmlFor="domain-body-editor">Detay içeriği</label>
                  <RichTextEditor
                    id="domain-body-editor"
                    key={`${model.id}-${locale}-${model.kind}`}
                    name="body"
                    defaultValue={body}
                    onChange={(value) =>
                      change((current) =>
                        current.kind === "editorial"
                            ? {
                                ...current,
                                content: { ...current.content, body: value },
                              }
                            : current,
                      )
                    }
                  />
                </div>
              </div>
            </section>
          </Tabs.Content>
          <Tabs.Content value={model.kind === "editorial" ? "related" : "structure"} className="editor-tab-panel">
            <section className={editorStyles.section}>
              <div className="section-intro">
                <h2>Yapı ve ilişkiler</h2>
                <p>
                  İçeriğin katalogdaki konumunu ve diğer kayıtlarla bağını
                  yönetin.
                </p>
              </div>
              <div className="form-grid">
                <Field label="İç kod">
                  <input
                    className="ui-input"
                    value={model.id}
                    disabled
                  />
                </Field>
                <Field label="Liste sırası">
                  <input
                    className="ui-input"
                    type="number"
                    value={model.record.order}
                    onChange={(event) =>
                      change(
                        (current) =>
                          ({
                            ...current,
                            record: {
                              ...current.record,
                              order: Number(event.target.value),
                            },
                          }) as EditorModel,
                      )
                    }
                  />
                </Field>
                {model.kind === "editorial" && (
                  <Field label="İçerik formatı">
                    <select
                      className="ui-select"
                      value={model.record.format}
                      onChange={(event) =>
                        change((current) =>
                          current.kind === "editorial"
                            ? {
                                ...current,
                                record: {
                                  ...current.record,
                                  format: event.target.value,
                                },
                              }
                            : current,
                        )
                      }
                    >
                      <option value="article">Makale</option>
                      <option value="video">Video</option>
                      <option value="resource">Kaynak</option>
                      <option value="training">Eğitim</option>
                      <option value="external_news">Harici haber</option>
                    </select>
                  </Field>
                )}
                {model.kind === "editorial" && <label className="choice-card full">
                  <input
                    type="checkbox"
                    checked={model.record.featured}
                    onChange={(event) =>
                      change(
                        (current) =>
                          ({
                            ...current,
                            record: {
                              ...current.record,
                              featured: event.target.checked,
                            },
                          }) as EditorModel,
                      )
                    }
                  />
                  <span>
                    <strong>Öne çıkar</strong>
                    <small>
                      İlgili içerik listelerinde daha görünür gösterilir.
                    </small>
                  </span>
                </label>}
              </div>
              {model.kind === "editorial" ? <DomainRelations model={model} change={updater=>change(current=>current.kind === "editorial" ? updater(current) : current)} /> : null}
            </section>
          </Tabs.Content>
          {model.kind === "editorial" ? (() => {
            const section = model.content.sections.find((item) => item.key === "video");
            if (!section) return null;
            return <Tabs.Content value="video" className="editor-tab-panel"><section className={editorStyles.section}>
              <div className="section-intro"><h2>Video</h2><p>Bu bölümü sayfa sırasına göre yönetin ve ortak YouTube oynatıcısını kullanın.</p></div>
              <div className="form-grid">
                <Field label="Üst etiket"><input className="ui-input" value={section.eyebrow} onChange={event=>change(current=>current.kind==="editorial"?({...current,content:{...current.content,sections:current.content.sections.map(item=>item.id===section.id?{...item,eyebrow:event.target.value}:item)}}):current)}/></Field>
                <Field label="Başlık"><input className="ui-input" value={section.title} onChange={event=>change(current=>current.kind==="editorial"?({...current,content:{...current.content,sections:current.content.sections.map(item=>item.id===section.id?{...item,title:event.target.value}:item)}}):current)}/></Field>
                <Field label="Açıklama" className="full"><textarea className="ui-textarea" value={section.description} onChange={event=>change(current=>current.kind==="editorial"?({...current,content:{...current.content,sections:current.content.sections.map(item=>item.id===section.id?{...item,description:event.target.value}:item)}}):current)}/></Field>
              </div>
              {section.video ? <VideoBlockEditor sectionTitle={section.title} value={section.video} onChange={video=>change(current=>current.kind==="editorial"?({...current,content:{...current.content,sections:current.content.sections.map(item=>item.id===section.id?{...item,video}:item)}}):current)}/> : null}
            </section></Tabs.Content>;
          })() : null}
          <Tabs.Content value="media" className="editor-tab-panel">
            <section className={editorStyles.section}>
              <div className="section-intro">
                <h2>Kapak ve erişilebilirlik</h2>
                <p>
                  Medya kütüphanesindeki varlığı kullanın ve açıklayıcı
                  alternatif metin sağlayın.
                </p>
              </div>
              <MediaPicker
                assetId={model.record.mediaAssetId}
                imageUrl={model.image}
                onSelect={(mediaAssetId, nextImage) =>
                  change(
                    (current) =>
                      ({
                        ...current,
                        image: nextImage,
                        record: { ...current.record, mediaAssetId },
                      }) as EditorModel,
                  )
                }
              />
              <Field label="Görsel alternatif metni">
                <input
                  className="ui-input"
                  value={model.translation.imageAlt}
                  onChange={(event) =>
                    change(
                      (current) =>
                        ({
                          ...current,
                          translation: {
                            ...current.translation,
                            imageAlt: event.target.value,
                          },
                        }) as EditorModel,
                    )
                  }
                />
              </Field>
            </section>
          </Tabs.Content>
          <Tabs.Content value="seo" className="editor-tab-panel">
            <section className={`${editorStyles.section} ${editorStyles.sectionNarrow}`}>
              <div className="section-intro">
                <h2>Arama görünümü</h2>
                <p>
                  Başlık ve açıklamayı sitedeki gerçek içerikle uyumlu
                  tutun.
                </p>
              </div>
              <Field label="SEO başlığı">
                <input
                  className="ui-input"
                  value={model.translation.seoTitle}
                  maxLength={200}
                  onChange={(event) =>
                    change(
                      (current) =>
                        ({
                          ...current,
                          translation: {
                            ...current.translation,
                            seoTitle: event.target.value,
                          },
                        }) as EditorModel,
                    )
                  }
                />
              </Field>
              <Field label="SEO açıklaması">
                <textarea
                  className="ui-textarea"
                  value={model.translation.seoDescription}
                  maxLength={500}
                  onChange={(event) =>
                    change(
                      (current) =>
                        ({
                          ...current,
                          translation: {
                            ...current.translation,
                            seoDescription: event.target.value,
                          },
                        }) as EditorModel,
                    )
                  }
                />
              </Field>
            </section>
          </Tabs.Content>
          <Tabs.Content value="preview" className="editor-tab-panel">
            <section className={editorStyles.section}>
              <div className="section-intro">
                <h2>Canlı site önizlemesi</h2>
                <p>Kaydedilmiş taslağı mevcut ekran boyutlarında inceleyin.</p>
              </div>
              <DraftPreview
                kind={mode === "referenceCompany" ? "reference_company" : mode}
                id={model.id}
                locale={locale}
                disabled={dirty || save.isPending}
              />
            </section>
          </Tabs.Content>
          </>}
          </div>
        </div>
      </Tabs.Root>
      <Drawer
        open={publishDrawerOpen}
        onOpenChange={setPublishDrawerOpen}
        title={reviewMode ? "İnceleme özeti" : "Yayın özeti"}
        side="right"
        variant="workspace"
      >
        <div className={editorStyles.drawer}>
          <header className={editorStyles.drawerHeader}>
            <div>
              <span>Karar özeti</span>
              <h2>{reviewMode ? "İnceleme özeti" : "Yayın özeti"}</h2>
            </div>
            <IconButton label="Özeti kapat" onClick={() => setPublishDrawerOpen(false)}><X size={18} /></IconButton>
          </header>
          <div className={editorStyles.drawerBody}>
            <section className={editorStyles.railCard}>
              <h2>Yayın hazırlığı</h2>
              <p>{completedChecks}/{qualityChecks.length} temel kontrol tamamlandı.</p>
              <div className={editorStyles.progressTrack}><span className={editorStyles.progressBar} style={{ width: `${Math.round((completedChecks / qualityChecks.length) * 100)}%` }} /></div>
              <ul className={editorStyles.checklist}>
                {qualityChecks.map((item) => <li key={item.key} data-complete={item.complete}>{item.complete ? <CheckCircle2 size={15} /> : <Circle size={15} />}<span>{item.label}</span></li>)}
              </ul>
              {completedChecks < qualityChecks.length ? <button type="button" className={editorStyles.jumpButton} onClick={() => { changeSection(qualityChecks.find((item) => !item.complete)?.section || "content"); setPublishDrawerOpen(false); }}><CircleAlert size={14} /> Eksik alana git</button> : null}
            </section>
            <section className={editorStyles.railCard}>
              <h2>Kayıt özeti</h2>
              <p>Bu dil ve sürümün güncel durumu.</p>
              <dl className={editorStyles.railMeta}>
                <div><dt>Durum</dt><dd>{model.workflow.label}</dd></div>
                <div><dt>Dil</dt><dd>{languageState?.label || locale.toUpperCase()}</dd></div>
                <div><dt>Sürüm</dt><dd>{model.version}</dd></div>
                <div><dt>Değişiklik</dt><dd>{dirty ? "Kaydedilmedi" : "Güncel"}</dd></div>
              </dl>
              {model.workflow.reviewNotice ? <div className={editorStyles.reviewNotice} role="status"><CircleAlert size={16} />{model.workflow.reviewNotice}</div> : null}
            </section>
            <section className={editorStyles.railCard}>
              <h2>Revizyon geçmişi</h2>
              <p>Son taslak ve canlı sürümleri izleyin; gerektiğinde eski bir sürümden yeni taslak oluşturun.</p>
              <div className="revision-list">
                {model.revisions.map((revision) => (
                  <article key={revision.version}>
                    <div>
                      <strong>Sürüm {revision.version}</strong>
                      <span>{revision.createdBy} · {revision.createdAt.toLocaleString("tr-TR")}</span>
                      {revision.changedFields.length ? (
                        <span className={editorStyles.revisionChanges}>
                          {revisionChangeLabels(revision.changedFields).join(" · ")}
                        </span>
                      ) : null}
                    </div>
                    <div className={editorStyles.revisionActions}>
                      {revision.draft && <StatusBadge>Taslak</StatusBadge>}
                      {revision.live && <StatusBadge>Canlı</StatusBadge>}
                      {model.workflow.actions.includes("restore") && !revision.draft ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={restore.isPending}
                          onClick={() => restore.mutate(revision.version)}
                        >
                          Yeni taslak yap
                        </Button>
                      ) : null}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>
        </div>
      </Drawer>
      <AlertDialog
        open={Boolean(pendingLocale)}
        onOpenChange={(open) => !open && setPendingLocale(null)}
        title="Başka dile geçilsin mi?"
        description="Bu dilde kaydedilmemiş değişiklikler var. Değişiklikler kaydedildikten sonra seçtiğiniz dil açılacak."
        confirmLabel={save.isPending ? "Kaydediliyor…" : "Kaydet ve dile geç"}
        onConfirm={() => void confirmLocaleSwitch()}
      />
      <Dialog
        open={reviewNoticeOpen}
        onOpenChange={setReviewNoticeOpen}
        title="İstenen değişiklikler"
        description="İçeriği yeniden onaya göndermeden önce bu notu dikkate alın."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setReviewNoticeOpen(false)}>
              Kapat
            </Button>
            <WorkflowControls
              compact
              workflow={model.workflow}
              includeActions={["withdraw_change_request"]}
              immediateActions={["withdraw_change_request"]}
              disabled={Boolean(workflowDisabledReason)}
              disabledReason={workflowDisabledReason}
              onUpdate={(workflow) => {
                applyWorkflowUpdate(workflow);
                setReviewNoticeOpen(false);
              }}
              onError={setError}
            />
          </>
        }
      >
        <div className={editorStyles.reviewNoticeDialog}>
          <CircleAlert size={19} aria-hidden="true" />
          <p>{model.workflow.reviewNotice}</p>
        </div>
      </Dialog>
      <StickySaveBar
        tone={error ? "danger" : dirty || model.workflow.reviewNotice ? "warning" : "success"}
        status={error || (
          save.isPending
            ? "Kaydediliyor…"
            : dirty
              ? canSaveDraft(model.workflow.actions) ? "Değişiklikler otomatik kaydedilecek" : "Kaydedilmemiş değişiklik"
              : model.workflow.reviewNotice
                ? (
                  <button
                    type="button"
                    className={editorStyles.reviewNoticeButton}
                    onClick={() => setReviewNoticeOpen(true)}
                  >
                    <CircleAlert size={16} aria-hidden="true" />
                    İstenen değişiklikleri görüntüle
                  </button>
                )
                : message || "Tüm değişiklikler kayıtlı"
        )}
      >
        {canSaveDraft(model.workflow.actions) ? (
          <Button
            variant="secondary"
            onClick={() => save.mutate(model)}
            disabled={save.isPending || !dirty || Boolean(conflictServer)}
          >
            <Save size={17} />Taslağı kaydet
          </Button>
        ) : null}
        <WorkflowControls
          placement="footer"
          workflow={model.workflow}
          excludeActions={["withdraw_change_request"]}
          disabled={Boolean(workflowDisabledReason)}
          disabledReason={workflowDisabledReason}
          onUpdate={applyWorkflowUpdate}
          onError={setError}
        />
      </StickySaveBar>
    </EditorWorkspaceFrame>
  );
}
